import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import '@wangeditor/editor/dist/css/style.css' 
import './newspage.css'
import Navbar from '../../Home/navbar'
import img from '../../assets/logo4.jpeg' 

const NewsDisplay = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [fetchedArticle, setFetchedArticle] = useState(null)
  const [relatedNews, setRelatedNews] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingRelated, setIsLoadingRelated] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (id) {
      fetchArticle(id)
    }
    fetchRelatedNews()
  }, [id])
  
  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchArticle = async (articleId) => {
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`${apiUrl}/api/news/${articleId}`)
      
      if (!response.ok) {
        throw new Error('فشل في تحميل المقال')
      }
      
      const data = await response.json()
      
      if (data.success) {
        const hasImages = data.data.content?.includes('<img')
        if (hasImages) {
          const hasBase64Images = data.data.content?.includes('data:image/')   
          const imageCount = (data.data.content?.match(/<img/g) || []).length
        }
        
        setFetchedArticle(data.data)
      } else {
        throw new Error(data.error || 'فشل في تحميل المقال')
      }
    } catch (error) {
      setError(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchRelatedNews = async () => {
    setIsLoadingRelated(true)
    
    try {
      const response = await fetch(`${apiUrl}/api/news?limit=6`) 
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          const filtered = data.data
            .filter(item => item.id !== parseInt(id)) 
            .filter(item => item.category_id !== 4)  
          setRelatedNews(filtered.slice(0, 6))
        }
      }
    } catch (error) {
      console.error('Failed to load related news:', error)
    } finally {
      setIsLoadingRelated(false)
    }
  }

  const handleBack = () => {
    const returnPath = location.state?.returnPath;
    
    if (returnPath) {
      navigate(returnPath);
    } else if (window.history.length > 1 && window.document.referrer) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }

  const handleRelatedNewsClick = (relatedArticle) => {
    navigate(`/news/${relatedArticle.id}`)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const extractFirstImageFromContent = (content) => {
    if (!content) return null
    const imgRegex = /<img[^>]+src="([^"]+)"[^>]*>/i
    const match = content.match(imgRegex)
    return match ? match[1] : null
  }

  // Get main image URL (prioritize first content image, then main_image, then logo)
  const getMainImageUrl = (mainImage, content) => {
    // First try to get first image from content
    const firstContentImage = extractFirstImageFromContent(content)
    if (firstContentImage) {
      if (firstContentImage.startsWith('http://') || firstContentImage.startsWith('https://') || firstContentImage.startsWith('data:')) {
        return firstContentImage
      }
      const cleanPath = firstContentImage.startsWith('/') ? firstContentImage.substring(1) : firstContentImage
      return `${apiUrl}/storage/${cleanPath}`
    }

    // Then try main_image
    if (mainImage) {
      if (mainImage.startsWith('http://') || mainImage.startsWith('https://') || mainImage.startsWith('data:')) {
        return mainImage
      }
      const cleanPath = mainImage.startsWith('/') ? mainImage.substring(1) : mainImage
      return `${apiUrl}/storage/${cleanPath}`
    }
    
    // Default to logo
    return img
  }

  // Remove first image from content to avoid duplication
  const removeFirstImageFromContent = (content) => {
    if (!content) return content
    const imgRegex = /<img[^>]*>/i
    return content.replace(imgRegex, '').trim()
  }

  // Get image URL for related news - matches TrendingSection logic
  const getRelatedImageUrl = (newsItem) => {
    // First priority: first_content_image field (like TrendingSection)
    if (newsItem.first_content_image) {
      return newsItem.first_content_image;
    }

    // Second priority: main_image field  
    if (newsItem.main_image) {
      if (/^https?:\/\//i.test(newsItem.main_image) || newsItem.main_image.startsWith('data:')) {
        return newsItem.main_image;
      } else {
        const cleanPath = newsItem.main_image.startsWith('/') ? newsItem.main_image.slice(1) : newsItem.main_image;
        return `${apiUrl}/storage/${cleanPath}`;
      }
    }

    // Default to logo image if no image found
    return img;
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const truncateText = (text, maxLength = 120) => {
    if (!text) return ''
    const cleanText = text.replace(/<[^>]*>/g, '')
    return cleanText.length > maxLength 
      ? cleanText.substring(0, maxLength) + '...' 
      : cleanText
  }

  // Render the main layout immediately, only show loading in content area
  return (
    <div className="news-main-container">
      {/* Navbar loads immediately */}
      <div className="navbar-news">
        <Navbar />
      </div>
      
      <div className="news-container-page">
        {/* Back button loads immediately */}
        <button onClick={handleBack} className="news-back-btn-page">
          <svg
            className="news-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          العودة للأخبار
        </button>

        {/* Content area - this is where loading/error states appear */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p>جاري تحميل المقال...</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h3>خطأ في تحميل المقال</h3>
            <p>{error}</p>
            <button 
              onClick={() => id && fetchArticle(id)}
              style={{
                background: '#3498db',
                color: 'white',
                border: 'none',
                padding: '0.7rem 1.5rem',
                borderRadius: '5px',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              إعادة المحاولة
            </button>
          </div>
        ) : !fetchedArticle ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>المقال غير موجود</p>
          </div>
        ) : (
          // Article content - only renders when data is loaded
          <>
            <div className="news-header-page">
              <h1 className="news-title-page">{fetchedArticle.title}</h1>
              
              <div className="news-meta-page">
                <div className="news-category-page">
                  {/* Category content if needed */}
                </div>
                <div className="news-date-page">
                  <div>تاريخ النشر: {formatDate(fetchedArticle.created_at)}</div>
                </div>
              </div>
            </div>

            {/* Main image - Always show (first content image, main_image, or logo) */}
            <div className="news-main-image">
              <img 
                src={getMainImageUrl(fetchedArticle.main_image, fetchedArticle.content)} 
                alt={fetchedArticle.title}
                onError={(e) => {
                  console.error('Main image failed to load:', e.target.src)
                  e.target.src = img
                }}
              />
            </div>

            {/* Article content with first image removed to avoid duplication */}
            <div className="news-content-page">
              <div dangerouslySetInnerHTML={{ 
                __html: removeFirstImageFromContent(fetchedArticle.content) 
              }} />
            </div>

            <div className="div">
              <h2>حمل تطبيق الدار العراقية الان</h2>
              
              <div className="app-download-buttons">
                {/* Google Play Store */}
                <a 
                  href="https://play.google.com/store/apps/details?id=com.bytesstorm.inaiq" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                    alt="Download on Google Play" 
                    className="store-badge"
                  />
                </a>

                {/* Apple App Store */}
                <a 
                  href="https://apps.apple.com/lb/app/%D8%A7%D9%84%D8%AF%D8%A7%D8%B1-%D8%A7%D9%84%D8%B9%D8%B1%D8%A7%D9%82%D8%A9/id6752357412" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" 
                    alt="Download on App Store" 
                    className="store-badge"
                  />
                </a>
              </div>
            </div>

            {/* Related News Section */}
            {relatedNews.length > 0 && (
              <div className="related-news-section">
                <h2 className="related-news-title">أخبار أخرى</h2>
                
                {isLoadingRelated ? (
                  <div className="related-news-loading">
                    <p>جاري تحميل الأخبار...</p>
                  </div>
                ) : (
                  <div className="related-news-grid">
                    {relatedNews.map((newsItem) => (
                      <div 
                        key={newsItem.id} 
                        className="related-news-item"
                        onClick={() => handleRelatedNewsClick(newsItem)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="related-news-image">
                          <img 
                            src={getRelatedImageUrl(newsItem)}
                            alt={newsItem.title}
                            loading="lazy"
                            onError={(e) => {
                              console.error('Related news image failed:', e.target.src)
                              e.target.src = img
                            }}
                          />
                        </div>
                        <div className="related-news-content">
                          <h3 className="related-news-item-title">
                            {newsItem.title}
                          </h3>
                          <p className="related-news-excerpt">
                            {truncateText(newsItem.content)}
                          </p>
                          <div className="related-news-date">
                            {formatDate(newsItem.created_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div>
      </div>
    </div>
  )
}

export default NewsDisplay